/**
 * Format a date string to a more readable format
 * @param {String} dateString - Date string in ISO format
 * @returns {String} - Formatted date string
 */
export const formatDate = (dateString) => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Format a date-time string to a more readable format
 * @param {String} dateString - Date string in ISO format
 * @returns {String} - Formatted date-time string
 */
export const formatDateTime = (dateString) => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Get color class for a status
 * @param {String} status - Status string
 * @returns {String} - CSS class name
 */
export const getStatusColorClass = (status) => {
  if (!status) return 'status-unknown';
  
  const statusLower = status.toLowerCase();
  
  switch (statusLower) {
    case 'approved':
      return 'status-approved';
    case 'active':
      return 'status-active';
    case 'resolved':
      return 'status-resolved';
    case 'closed':
      return 'status-closed';
    case 're-opened':
      return 'status-reopened';
    case 'new':
      return 'status-new';
    case 'in progress':
      return 'status-in-progress';
    case 'ready for test':
      return 'status-ready-for-test';
    case 'ready for review':
      return 'status-ready-for-review';
    case 'blocked':
      return 'status-blocked';
    case 'deferred':
      return 'status-deferred';
    default:
      return 'status-unknown';
  }
};

/**
 * Get color for a status (for charts)
 * @param {String} status - Status string
 * @returns {String} - Hex color code
 */
export const getStatusColor = (status) => {
  if (!status) return '#CCCCCC';
  
  const statusLower = status.toLowerCase();
  
  switch (statusLower) {
    case 'approved':
      return '#CCCCCC'; // Grey
    case 'active':
      return '#0000A0'; // Dark Blue
    case 'resolved':
      return '#90EE90'; // Light Green
    case 'closed':
      return '#00A000'; // Green
    case 'done':
      return '#00A000'; // Green
    case 'completed':
      return '#00A000'; // Green
    case 're-opened':
      return '#FFA500'; // Orange
    case 'new':
      return '#FFA500'; // Amber
    case 'in progress':
      return '#FFC000'; // Yellow
    case 'ready for test':
      return '#ED7D31'; // Orange
    case 'ready for review':
      return '#5B9BD5'; // Light Blue
    case 'blocked':
      return '#FF0000'; // Red
    case 'deferred':
      return '#7030A0'; // Purple
    default:
      return '#CCCCCC'; // Gray
  }
};

/**
 * Get color for a priority (for charts)
 * @param {Number} priority - Priority number
 * @returns {String} - Hex color code
 */
export const getPriorityColor = (priority) => {
  switch (priority) {
    case 1:
      return '#FF0000'; // Red
    case 2:
      return '#FFA500'; // Orange
    case 3:
      return '#00B050'; // Green
    case 4:
      return '#0070C0'; // Blue
    default:
      return '#CCCCCC'; // Gray
  }
};

/**
 * Get color class for completion percentage
 * @param {Number} percentage - Completion percentage
 * @returns {String} - CSS class name
 */
export const getCompletionColorClass = (percentage) => {
  if (percentage < 33) return 'completion-low';
  if (percentage < 67) return 'completion-medium';
  return 'completion-high';
};

/**
 * Format a number as a percentage
 * @param {Number} value - Value to format
 * @returns {String} - Formatted percentage string
 */
export const formatPercentage = (value) => {
  return `${Math.round(value)}%`;
};

/**
 * Truncate a string to a maximum length
 * @param {String} str - String to truncate
 * @param {Number} maxLength - Maximum length
 * @returns {String} - Truncated string
 */
export const truncateString = (str, maxLength = 50) => {
  if (!str) return '';
  if (str.length <= maxLength) return str;
  return `${str.substring(0, maxLength)}...`;
};

/**
 * Format a work item ID
 * @param {String} id - Work item ID
 * @returns {String} - Formatted ID
 */
export const formatWorkItemId = (id) => {
  if (!id) return '';
  return `#${id}`;
};

/**
 * Get the initials from a name
 * @param {String} name - Full name
 * @returns {String} - Initials
 */
export const getInitials = (name) => {
  if (!name) return '';
  
  return name
    .split(' ')
    .map(part => part.charAt(0))
    .join('')
    .toUpperCase()
    .substring(0, 2);
};

/**
 * Extract team name from area path
 * @param {String} areaPath - Area path
 * @returns {String} - Team name
 */
export const extractTeamFromAreaPath = (areaPath) => {
  if (!areaPath) return '';
  
  const parts = areaPath.split('\\');
  return parts.length > 1 ? parts[1] : '';
};

/**
 * Format a fix version
 * @param {String} version - Fix version
 * @returns {String} - Formatted version
 */
export const formatFixVersion = (version) => {
  if (!version) return 'N/A';
  return version;
};

/**
 * Get the quarter for a date
 * @param {String|Date} date - Date string or Date object
 * @returns {Object} - Quarter information { year, quarter, label }
 */
export const getQuarter = (date) => {
  if (!date) return null;
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const year = dateObj.getFullYear();
  const month = dateObj.getMonth();
  
  // Determine quarter (0-based months: 0-2 = Q1, 3-5 = Q2, 6-8 = Q3, 9-11 = Q4)
  const quarter = Math.floor(month / 3) + 1;
  
  return {
    year,
    quarter,
    label: `${year}-Q${quarter}`
  };
};

/**
 * Get quarters between two dates
 * @param {String|Date} startDate - Start date string or Date object
 * @param {String|Date} endDate - End date string or Date object
 * @returns {Array} - Array of quarter objects { year, quarter, label }
 */
export const getQuartersBetweenDates = (startDate, endDate) => {
  if (!startDate || !endDate) return [];
  
  const startObj = typeof startDate === 'string' ? new Date(startDate) : startDate;
  const endObj = typeof endDate === 'string' ? new Date(endDate) : endDate;
  
  const startQuarter = getQuarter(startObj);
  const endQuarter = getQuarter(endObj);
  
  const quarters = [];
  
  // Loop through all quarters between start and end
  for (let year = startQuarter.year; year <= endQuarter.year; year++) {
    const startQ = (year === startQuarter.year) ? startQuarter.quarter : 1;
    const endQ = (year === endQuarter.year) ? endQuarter.quarter : 4;
    
    for (let quarter = startQ; quarter <= endQ; quarter++) {
      quarters.push({
        year,
        quarter,
        label: `${year}-Q${quarter}`
      });
    }
  }
  
  return quarters;
};

/**
 * Get the current quarter's start and end dates
 * @returns {Object} - Object with startDate and endDate properties
 */
export const getCurrentQuarterDates = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  
  // Determine current quarter (0-based months: 0-2 = Q1, 3-5 = Q2, 6-8 = Q3, 9-11 = Q4)
  const quarter = Math.floor(month / 3) + 1;
  
  // Calculate start and end dates
  const startMonth = (quarter - 1) * 3;
  const endMonth = startMonth + 2;
  
  const startDate = new Date(year, startMonth, 1);
  const endDate = new Date(year, endMonth + 1, 0); // Last day of the end month
  
  // Format dates as YYYY-MM-DD
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  return {
    startDate: formatDate(startDate),
    endDate: formatDate(endDate),
    quarter: {
      year,
      quarter,
      label: `${year}-Q${quarter}`
    }
  };
};
